import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/firestore";
import firebase from "firebase";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";

interface Condition {
  left: string;
  operator: any;
  right: any;
}

@Injectable({
  providedIn: "root",
})
export class DataAccessHelpersService {
  constructor(private afs: AngularFirestore) {}
  /**
   * Returns an observable of a firestore document
   * @param docPath Firestore document path
   */
  getDocument(docPath: string): Observable<any> {
    return this.afs
      .doc(docPath)
      .snapshotChanges()
      .pipe(
        map((a) => {
          const data = a.payload.data() as any;
          const id = a.payload.id;
          const path = a.payload.ref.path;
          return { id, path, ...data };
        }),
      );
  }
  /**
   * Returns a Promise of a firestore collection
   * @param collectionPath Firestore collection path
   */
  getCollectionOnce(collectionPath: string): Promise<any[]> {
    return this.afs
      .collection(collectionPath)
      .ref.get()
      .then((docs) => {
        const result = [];
        docs.forEach((doc) => {
          const id = doc.id;
          const data = doc.data() as any;
          const path = doc.ref.path;
          result.push({ id, path, ...data });
        });
        return result;
      });
  }
  getCollectionSnapshot(
    collectionPath: string,
    callback: (data: any[]) => void,
  ): Subscription {
    return this.afs
      .collection(collectionPath)
      .snapshotChanges()
      .subscribe((snapshots) => {
        const result = snapshots.map((snapshot) => {
          const data = snapshot.payload.doc.data() as any;
          return {
            id: snapshot.payload.doc.id,
            path: snapshot.payload.doc.ref.path,
            ...data,
          };
        });
        callback(result);
      });
  }
  getCollectionOnceOrderBy(
    collectionPath: string,
    queryFn?: (
      ref: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>,
    ) => firebase.firestore.Query<firebase.firestore.DocumentData>,
  ): Promise<any[]> {
    let collectionRef:
      | firebase.firestore.CollectionReference<firebase.firestore.DocumentData>
      | firebase.firestore.Query<firebase.firestore.DocumentData> =
      this.afs.collection(collectionPath).ref;
    if (queryFn) {
      collectionRef = queryFn(
        collectionRef as firebase.firestore.CollectionReference<firebase.firestore.DocumentData>,
      );
    }
    return collectionRef.get().then((docs) => {
      const result: any[] = [];
      docs.forEach((doc) => {
        const id = doc.id;
        const data = doc.data();
        const path = doc.ref.path;
        result.push({ id, path, ...data });
      });
      return result;
    });
  }
  /**
   * Returns a Promise of a firestore document
   * @param docPath Firestore document path
   */
  getDocumentOnce(docPath: string): Promise<any> {
    return this.afs
      .doc(docPath)
      .ref.get()
      .then((doc) => {
        const data = doc.data() as any;
        const id = doc.id;
        const path = doc.ref.path;
        return { id, path, ...data };
      });
  }
  /**
   * Returns an observable of a firestore collection
   * @param coPath Firestore collection path
   */
  getCollection(coPath: string): Observable<any[]> {
    return this.afs
      .collection<any>(coPath)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return { id, path, ...data };
          });
        }),
      );
  }
  getLastMonthCollection(coPath: string): Observable<any[]> {
    const last31Days = new Date();
    last31Days.setDate(last31Days.getDate() - 3);
    const last31DaysMs = last31Days.getTime();

    const data$ = this.afs
      .collection<any>(coPath, (ref) =>
        ref.where("createdAt", ">=", last31DaysMs),
      )
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return { id, path, ...data };
          }),
        ),
      );
    // data$.subscribe(
    //   (data) => console.log("Data", data),
    //   (err) => console.error(err),
    // );
    return data$;
  }
  getCollectionOrderBy(
    coPath: string,
    orderBy: string,
    direc: firebase.firestore.OrderByDirection,
  ): Observable<any[]> {
    return this.afs
      .collection<any>(coPath, (ref) => {
        if (orderBy) {
          return ref.orderBy(orderBy, direc);
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return { id, path, ...data };
          });
        }),
      );
  }
  /**
   * Returns an observable of a firestore collection with a given condition(s)
   * @param coPath Firestore collection path
   * @param conditions Array of conditions (Queries)
   */
  getCollectionWhere(
    coPath: string,
    conditions: Condition[],
  ): Observable<any[]> {
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditions.pop();
        if (cnd0) {
          let query = ref.where(cnd0.left, cnd0.operator, cnd0.right);
          conditions.forEach(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return { id, path, ...data };
          });
        }),
      );
  }
  getCollectionWhereByOrder(
    coPath: string,
    conditions: Condition[],
    orderBy: string,
    direc: firebase.firestore.OrderByDirection,
  ): Observable<any[]> {
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditions.pop();
        if (cnd0) {
          let query = ref.where(cnd0.left, cnd0.operator, cnd0.right);
          conditions.forEach(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          query = query.orderBy(orderBy, direc);
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return { id, path, ...data };
          });
        }),
      );
  }

  getCollectionWhereByOrderWithLimit(
    coPath: string,
    conditions: Condition[],
    orderBy: string,
    direc: firebase.firestore.OrderByDirection,
    limit: number,
  ): Observable<any[]> {
    return this.afs
      .collection<any>(coPath, (ref) => {
        const cnd0 = conditions.pop();
        if (cnd0) {
          let query = ref.where(cnd0.left, cnd0.operator, cnd0.right);
          conditions.forEach(({ left, operator, right }: Condition) => {
            query = query.where(left, operator, right);
          });
          query = query.orderBy(orderBy, direc);
          query = query.limit(limit);
          return query;
        } else {
          return ref;
        }
      })
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as any;
            const id = a.payload.doc.id;
            const path = a.payload.doc.ref.path;
            return { id, path, ...data };
          });
        }),
      );
  }

  private firstClientSubject = new BehaviorSubject<string | null>(null);
  firstClient$ = this.firstClientSubject.asObservable(); // Observable for subscription

  // Set the value of firstClient
  setFirstClient(client: string): void {
    this.firstClientSubject.next(client);
  }

  // Get the current value of firstClient
  getFirstClient(): string | null {
    return this.firstClientSubject.getValue();
  }
  private API_KEY = '448a630e887bcc2ac3fc1dc45c5e1b06';
  private TOKEN = '8d07a63d0292f07ebcddb51fc709aa6691e7d1017d4e5c3ba6f2b1e7ac045303';
  private baseUrl = 'https://api.trello.com/1';

/**
 * Récupère toutes les listes d'un board
 */
async getBoardLists(boardId: string): Promise<any[]> {
  const url = `${this.baseUrl}/boards/${boardId}/lists?key=${this.API_KEY}&token=${this.TOKEN}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur dans getBoardLists :', error);
    throw error;
  }
}

/**
 * Récupère toutes les cartes d'une liste donnée
 */
async getCardsInList(listId: string): Promise<any[]> {
  const url = `${this.baseUrl}/lists/${listId}/cards?key=${this.API_KEY}&token=${this.TOKEN}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur dans getCardsInList :', error);
    throw error;
  }
}

/**
 * Récupère toutes les cartes du board (fusion des listes "En cours" et "Terminé")
 * C'est l'équivalent du useEffect du contexte React.
 */
  async loadBoardCards(boardId: string): Promise<any[]> {
  try {
    // 1. Récupérer les listes
    const lists = await this.getBoardLists(boardId);
    if (lists.length < 3) {
      throw new Error('Le board doit contenir au moins 3 listes');
    }

    // 2. Récupérer les cartes des 2ème et 3ème listes
    const listId = lists[1].id;
    const closedListId = lists[2].id;

    const [cards, closedCards] = await Promise.all([
      this.getCardsInList(listId),
      this.getCardsInList(closedListId)
    ]);

    // 3. Fusionner et formater (comme dans React)
    const allCards = [...closedCards, ...cards].map((el: any) => {
      const trelloTimestamp = 1000 * parseInt(el.id.substring(0, 8), 16);
      return {
        ...el,
        due: new Date(trelloTimestamp),                      // Date object
      dateLastActivity: el.dateLastActivity ? new Date(el.dateLastActivity) : null,
      sortingTimeVar: trelloTimestamp
    };
  });
  // 4. Trier par date décroissante
  return allCards.sort((a, b) => b.sortingTimeVar - a.sortingTimeVar);
}

    
    
   catch (error) {
    console.error('Erreur dans loadBoardCards :', error);
    throw error;
  }
}

/**
 * Récupère le nombre total de cartes d'un board (pour l'index)
 */
async getBoardCardsCount(boardId: string): Promise<number> {
  const url = `${this.baseUrl}/boards/${boardId}/cards?key=${this.API_KEY}&token=${this.TOKEN}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }
    const cards = await response.json();
    return cards.length;
  } catch (error) {
    console.error('Erreur dans getBoardCardsCount :', error);
    throw error;
  }
}

/**
 * Crée une nouvelle carte dans une liste donnée
 */
async createCard(listId: string, name: string, desc: string): Promise<any> {
  const url = `${this.baseUrl}/cards?key=${this.API_KEY}&token=${this.TOKEN}&idList=${listId}&name=${encodeURIComponent(name)}&desc=${encodeURIComponent(desc)}`;
  try {
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur dans createCard :', error);
    throw error;
  }
}

/**
 * Ajoute un label à une carte
 */
async addLabelToCard(cardId: string, color: string, name: string): Promise<any> {
  const url = `${this.baseUrl}/cards/${cardId}/labels?key=${this.API_KEY}&token=${this.TOKEN}&color=${color}&name=${encodeURIComponent(name)}`;
  try {
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur dans addLabelToCard :', error);
    throw error;
  }
}

/**
 * Ajoute une pièce jointe à une carte (fichier)
 */
async attachFileToCard(cardId: string, file: File): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${this.baseUrl}/cards/${cardId}/attachments?key=${this.API_KEY}&token=${this.TOKEN}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP : ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Erreur dans attachFileToCard :', error);
    throw error;
  }
}
}
